import { useEffect, useMemo, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Card, Group, Input, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { ClaveSat } from "@/lib/types";
import { useClavesSatAdmin } from "../hooks/use-claves-sat-admin";
import { listarClavesSatAdmin } from "../services/claves-sat-admin-service";
import { CatalogosSatHeader } from "./CatalogosSatHeader";
import { ClaveSatCrearForm } from "./ClaveSatCrearForm";
import { ClavesSatTable } from "./ClavesSatTable";

const PAGE_SIZE = 15;

/**
 * Página de administración del catálogo SAT · búsqueda y paginado se
 * resuelven en el SERVIDOR vía GET /api/admin/claves-sat (con debounce).
 * El picker del wizard sigue con /api/catalogos/claves-sat · las mutations
 * invalidan ambas keys así las dos vistas refrescan.
 */
export function AdminCatalogosSatPage() {
  const [filtro, setFiltro] = useState("");
  const [modoCrear, setModoCrear] = useState(false);
  const [page, setPage] = useState(1);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  const filtrosServer = useMemo(
    () => (filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
    [filtroDebounced]
  );

  useEffect(() => {
    setPage(1);
  }, [filtroDebounced]);

  const { data } = useClavesSatAdmin({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const claves = data?.items ?? [];
  const total = data?.total ?? 0;

  const obtenerTodas = async (): Promise<ClaveSat[]> => {
    const todas: ClaveSat[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarClavesSatAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <CatalogosSatHeader total={data?.totalCatalogo ?? 0} modoCrear={modoCrear} onIniciarCrear={() => setModoCrear(true)} />
      <Stack gap="md">
        {modoCrear ? <ClaveSatCrearForm onSalir={() => setModoCrear(false)} /> : null}
        <Card padding="lg">
          <Stack gap="md">
            <Group align="flex-end" gap="md" wrap="wrap">
              <Input
                label="Buscar"
                placeholder="clave o descripción…"
                leftSection={<IconSearch size={14} stroke={1.7} />}
                value={filtro}
                onChange={(e) => setFiltro(e.currentTarget.value)}
                wrapperClassName="min-w-70 flex-1"
              />
            </Group>
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <ClavesSatTable claves={claves} obtenerFilas={obtenerTodas} />
            <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="claves" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
